import { Button, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import { useAuth } from "@stardeck-customer-apps/project-auth";
import { RefreshCwIcon } from "lucide-react";
import { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  useRegistrationList,
  useRegistrationStats,
  useRegistrationTimeline,
} from "@/lib/queries/use-registration";
import { useTestAuthApi } from "@/lib/queries/use-test-auth-api";
import AdminNav from "@/pages/admin/admin-nav";

const PIE_COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7f7f",
  "#8dd1e1",
  "#a4de6c",
  "#d0ed57",
  "#bc80bd",
];

export default function AdminDashboard() {
  useAuth();
  useTestAuthApi();
  const { data: stats, isLoading, refetch, isFetching } = useRegistrationStats();
  const [timeRange, setTimeRange] = useState<"today" | "7d">("7d");
  const [granularity, setGranularity] = useState<"daily" | "hourly">("daily");
  const { data: timelineData, isLoading: timelineLoading } = useRegistrationTimeline(
    timeRange,
    granularity
  );
  const { data: listData } = useRegistrationList({ page: 1, limit: 1000 });

  const bankPie = useMemo(() => {
    const counts: Record<string, number> = {};
    const items = listData?.items ?? [];
    for (const r of items) {
      for (const b of r.banks || []) {
        counts[b] = (counts[b] || 0) + 1;
      }
    }
    const entries = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }));
    return entries;
  }, [listData]);

  // Format data for chart with cumulative values
  const chartData = useMemo(() => {
    if (!timelineData?.timeline) return [];

    let cumulativePrinted = 0;
    let cumulativeGifts = 0;

    return timelineData.timeline.map((point) => {
      cumulativePrinted += point.coupons_printed;
      cumulativeGifts += point.gifts_distributed;

      // Format label based on granularity
      let label: string;
      const date = new Date(point.time_period);

      if (granularity === "hourly") {
        label = date.toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "numeric",
          hour12: true,
        });
      } else {
        label = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      }

      return {
        date: label,
        "Coupons Printed": cumulativePrinted,
        "Gifts Distributed": cumulativeGifts,
        Registrations: point.registrations,
      };
    });
  }, [timelineData, granularity]);

  return (
    <div>
      <AdminNav />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCwIcon className={`size-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Registrations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? "..." : (stats?.totalRegistrations ?? 0).toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Today's Registrations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? "..." : (stats?.todayRegistrations ?? 0).toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Gifts Distributed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? "..." : (stats?.giftsDistributed ?? 0).toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Coupons Ready
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? "..." : (stats?.couponsReady ?? 0).toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Sales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? "..." : `฿${(stats?.totalSales ?? 0).toLocaleString("en-US")}`}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Today's Sales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? "..." : `฿${(stats?.todaySales ?? 0).toLocaleString("en-US")}`}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button asChild>
              <a href="/admin/registrations">View All Registrations</a>
            </Button>
            <Button asChild variant="outline">
              <a href="/admin/scanner">QR Scanner</a>
            </Button>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Bank Interest</CardTitle>
            </CardHeader>
            <CardContent>
              {!bankPie || bankPie.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-muted-foreground">
                  No data
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={bankPie}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={110}
                    >
                      {bankPie.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <CardTitle>Activity Timeline</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={timeRange === "today" ? "default" : "outline"}
                      onClick={() => setTimeRange("today")}
                    >
                      Today
                    </Button>
                    <Button
                      size="sm"
                      variant={timeRange === "7d" ? "default" : "outline"}
                      onClick={() => setTimeRange("7d")}
                    >
                      7d
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={granularity === "daily" ? "default" : "outline"}
                    onClick={() => setGranularity("daily")}
                  >
                    Daily
                  </Button>
                  <Button
                    size="sm"
                    variant={granularity === "hourly" ? "default" : "outline"}
                    onClick={() => setGranularity("hourly")}
                  >
                    Hourly
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {timelineLoading ? (
                <div className="h-80 flex items-center justify-center text-muted-foreground">
                  Loading...
                </div>
              ) : chartData.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      className="text-xs"
                      tick={{ fill: "hsl(var(--muted-foreground))" }}
                    />
                    <YAxis className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--background))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "6px",
                      }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Coupons Printed"
                      stackId="1"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary))"
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="Gifts Distributed"
                      stackId="2"
                      stroke="hsl(142, 76%, 36%)"
                      fill="hsl(142, 76%, 36%)"
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {granularity === "hourly" ? "Hourly" : "Daily"} Registrations
                  </CardTitle>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={granularity === "daily" ? "default" : "outline"}
                    onClick={() => setGranularity("daily")}
                  >
                    Daily
                  </Button>
                  <Button
                    size="sm"
                    variant={granularity === "hourly" ? "default" : "outline"}
                    onClick={() => setGranularity("hourly")}
                  >
                    Hourly
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {timelineLoading ? (
                <div className="h-80 flex items-center justify-center text-muted-foreground">
                  Loading...
                </div>
              ) : chartData.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      className="text-xs"
                      tick={{ fill: "hsl(var(--muted-foreground))" }}
                    />
                    <YAxis className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--background))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "6px",
                      }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Registrations"
                      stroke="hsl(221, 83%, 53%)"
                      fill="hsl(221, 83%, 53%)"
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
